import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { isEqual } from 'lodash';

import container from 'containers/SearchFormContainer';
import SearchFormMobile from 'components/search/SearchFormMobile';
import SearchFormDesktop from 'components/search/SearchFormDesktop';

import siteStyle from 'utils/siteStyle';
import { features } from 'utils/features';
import { useScreen } from 'hooks/useDimensions';

const Search = ({
  wrapperStyles,
  className,
  currentLocation,
  location,
  performSearch,
  setCart,
  setDate,
  setIsRecommendedDate,
  setLocation,
  setPrice,
  setPlayers,
  setRate,
  setSortByCourses,
  setSortByTimeSlots,
  setTime,
  date,
  locationFilter,
  dateFilter,
  searchFormError,
  forcedTargetEntity,
  initialAtCurrentLocation,
  withHeader,
  withMobileForm,
  isSticky,
  isHomePage,
  isFloatingSearchBar,
}) => {
  const { isTabletScreen, isMobileScreen } = useScreen();

  const isSearchDirty = useMemo(() => {
    if (!locationFilter || !dateFilter || !location || !date) return false;
    if (location.id !== locationFilter.id || !isEqual(dateFilter, date)) return true;
    return false;
  }, [location, locationFilter, date, dateFilter]);

  const onSearch = (afterSuccessCallback) => {
    setSortByCourses(null);
    setSortByTimeSlots('time');
    setCart(null);
    setRate(null);
    setPrice(null);
    setTime(null);
    setPlayers(null);
    setIsRecommendedDate(false);
    performSearch(forcedTargetEntity, undefined, true, afterSuccessCallback);
  };

  const renderHeader = (
    <>
      <h1 className={siteStyle({
        common: 'text-7 leading-8 -tracking-0.4 md:text-8 md:-tracking-0.2 lg:text-10 lg:leading-10 lg:-tracking-0.4',
        bt: 'font-primary font-black text-white',
        foh: 'font-rajdhani font-bold text-white',
        // bt: 'font-korolav font-bold text-white uppercase',
        default: 'font-primary font-black text-dark',
      })}
      >
        Find Your Next Tee Time
      </h1>
      <h2
        className={siteStyle({
          common: 'text-4 leading-5 -tracking-0.2 mt-2 pb-6',
          bt: 'text-white',
          foh: 'font-roboto text-white font-normal',
          default: 'font-primary text-grey-3 font-medium',
        })}
      >
        Search Millions of Tee Times at Once
      </h2>
    </>
  );

  const styles = useMemo(() => {
    if (isHomePage) {
      if (isMobileScreen) {
        return null;
      }

      if (isTabletScreen) {
        return {
          wrapperStyles: {
            marginTop: '72px', position: 'sticky', top: '-60px', zIndex: '11',
          },
          stickyContainer: {
            position: 'sticky', top: '112px', zIndex: '10',
          },
          searchFormDesktop: {
            paddingBottom: '16px',
          },
        };
      }
      if (!features?.hasNavMenu) {
        return {
          wrapperStyles: {
            marginTop: '72px', position: 'sticky', top: '-68px', zIndex: '11',
          },
          stickyContainer: {
            position: 'sticky', top: '112px', zIndex: '10',
          },
          searchFormDesktop: {
            paddingBottom: '16px',
          },
        };
      }

      return {
        wrapperStyles: {
          marginTop: '137px', position: 'sticky', top: '-4px', zIndex: '11',
        },
        stickyContainer: {
          position: 'sticky', top: '176px', zIndex: '10',
        },
        searchFormDesktop: {
          paddingBottom: '16px',
        },
      };
    }
    return null;
  }, [isHomePage, isTabletScreen, isMobileScreen]);

  return (
    <>
      <section
        style={styles?.wrapperStyles}
        className={siteStyle({
          common: wrapperStyles,
          bt: isSticky ? 'font-primary bg-secondary' : 'font-primary bg-white',
          // bt: isSticky ? 'font-korolav bg-secondary' : 'font-korolav bg-white',
          foh: isSticky ? 'font-primary bg-secondary' : 'font-primary bg-white',
          default: 'font-primary bg-white',
        })}
      >
        <div className={className}>
          {withHeader && renderHeader}
          {withMobileForm && (
            <div className="md:hidden">
              <SearchFormMobile
                date={date}
                setDate={setDate}
                currentLocation={currentLocation}
                location={location}
                setLocation={setLocation}
                onSearch={onSearch}
                isDirty={isSearchDirty}
                isFloatingSearchBar={isFloatingSearchBar}
              />
            </div>
          )}
          <div className="hidden md:block" style={styles?.searchFormDesktop}>
            <SearchFormDesktop
              date={date}
              setDate={setDate}
              location={location}
              setLocation={setLocation}
              onSearch={onSearch}
              searchFormError={searchFormError}
              initialAtCurrentLocation={initialAtCurrentLocation}
              isDirty={isSearchDirty}
            />
          </div>
        </div>
      </section>
      {isSticky && (
        <div style={styles?.stickyContainer}>
          <div
            className={siteStyle({
              common: 'h-9 md:h-12 w-full',
              bt: 'bg-secondary',
              foh: 'bg-secondary',
              default: 'bg-white',
            })}
          />
          <div
            className={siteStyle({
              common: 'border-t pointer-events-none',
              bt: 'border-secondary',
              foh: 'border-secondary',
              default: 'border-light-grey-four',
            })}
            style={{ top: '100%', height: '40px', backgroundImage: 'linear-gradient(#0000000a, #ffffff00)' }}
          />
        </div>
      )}
    </>
  );
};

Search.defaultProps = {
  wrapperStyles: 'relative py-8 px-4 flex justify-center md:px-10 md:py-16 lg:px-14 2xl:px-0',
  className: 'w-full max-w-300',
  withHeader: false,
  isSticky: false,
  withMobileForm: false,
  currentLocation: null,
  forcedTargetEntity: '',
  location: null,
  locationFilter: null,
  dateFilter: new Date(),
  date: new Date(),
  initialAtCurrentLocation: false,
  isHomePage: false,
  isFloatingSearchBar: false,
};

Search.propTypes = {
  wrapperStyles: PropTypes.string,
  className: PropTypes.string,
  isSticky: PropTypes.bool,
  withHeader: PropTypes.bool,
  withMobileForm: PropTypes.bool,
  currentLocation: PropTypes.shape({
    city: PropTypes.string,
    hierarchizedUrl: PropTypes.string,
    lat: PropTypes.number,
    lon: PropTypes.number,
    slug: PropTypes.string,
    label: PropTypes.string,
  }),
  date: PropTypes.instanceOf(Date),
  forcedTargetEntity: PropTypes.string,
  location: PropTypes.shape({
    id: PropTypes.number,
    label: PropTypes.string,
    hierarchizedUrl: PropTypes.string,
  }),
  locationFilter: PropTypes.shape({
    id: PropTypes.number,
  }),
  dateFilter: PropTypes.instanceOf(Date),
  searchFormError: PropTypes.bool.isRequired,
  performSearch: PropTypes.func.isRequired,
  setCart: PropTypes.func.isRequired,
  setDate: PropTypes.func.isRequired,
  setPlayers: PropTypes.func.isRequired,
  setIsRecommendedDate: PropTypes.func.isRequired,
  setLocation: PropTypes.func.isRequired,
  setPrice: PropTypes.func.isRequired,
  setRate: PropTypes.func.isRequired,
  setSortByCourses: PropTypes.func.isRequired,
  setSortByTimeSlots: PropTypes.func.isRequired,
  setTime: PropTypes.func.isRequired,
  initialAtCurrentLocation: PropTypes.bool,
  isHomePage: PropTypes.bool,
  isFloatingSearchBar: PropTypes.bool,
};

export default container(Search);
